import { useState } from 'react';
import {
  Camera,
  Check,
  Clock,
  Container,
  MonitorPlay,
  Package,
  Plus,
  type LucideIcon,
} from 'lucide-react';
import {
  blueprintMatchesRepo,
  isBaseBlueprint,
  type CustomSandboxSize,
  type ResourceSize,
} from '@runloop/reflex-contract';
import {
  DEFAULT_SANDBOX_IDLE_TIME_MINUTES,
  SANDBOX_IDLE_TIME_OPTIONS,
  displayBlueprintName,
  type Blueprint,
  type Snapshot,
} from '@reflex/shared';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { Label } from '@reflex/ui/components/ui/label';
import { ConfigChip, ConfigChipField } from '@reflex/ui/components/agent/ConfigChip';
import {
  ConfigChipSelect,
  type ConfigChipSelectOption,
} from '@reflex/ui/components/agent/ConfigChipSelect';
import { cn } from '@reflex/ui/lib/utils';
import type { AgentEnvVar } from '@/lib/types';
import { DevboxSizeChip } from './DevboxSizeChip';
import { AgentGroupChipPicker, AgentLabelsChip } from './TaxonomyConfigChips';
import { snapshotIdFromPickerValue, snapshotPickerValue } from './snapshot-picker-value';
import { BlueprintRepoMismatchHint } from './BlueprintRepoMismatchHint';
import { AgentEnvVarRow } from './AgentEnvVarsEditor';
import type { AvailableSecretsOwner } from './env-vars/SecretRefPicker';

const NONE_BLUEPRINT = '__none__';

/**
 * Docs targets surfaced by the per-field `InlineDocsHint` buttons. Each points
 * at the first-party concept page that explains that specific control.
 * `InlineDocsHint` renders nothing when the docs plugin hasn't registered a
 * renderer, so these stay inert in stories/tests and on surfaces where docs
 * are disabled.
 */
const BLUEPRINT_DOCS_SLUG = 'concepts/blueprints#launching-with-a-blueprint';
const DEVBOX_SIZE_DOCS_SLUG = 'concepts/devboxes#devbox-sizes';
const CAPABILITIES_DOCS_SLUG = 'concepts/devboxes#capabilities';
const IDLE_TIME_DOCS_SLUG = 'concepts/devboxes#auto-suspend';
const ENV_VARS_DOCS_SLUG = 'concepts/agents#environment-variables';
const SECRETS_DOCS_SLUG = 'concepts/agents#secrets';

const IDLE_TIME_CUSTOM_SENTINEL = -1;

function BlueprintChip({
  blueprints,
  snapshots = [],
  value,
  onChange,
}: {
  blueprints: Blueprint[];
  /** Org devbox snapshots offered alongside blueprints. Empty hides the group. */
  snapshots?: Snapshot[];
  value: string;
  onChange: (value: string) => void;
}) {
  const selectedSnapshotId = snapshotIdFromPickerValue(value);
  const selectedSnapshot = selectedSnapshotId
    ? (snapshots.find((s) => s.id === selectedSnapshotId) ?? null)
    : null;
  const selected =
    !selectedSnapshotId && value !== NONE_BLUEPRINT
      ? (blueprints.find((bp) => bp.id === value) ?? null)
      : null;
  // Fall back to the raw id while the snapshot list is still loading (or the
  // snapshot was deleted) so the chip never claims "No blueprint" with a
  // snapshot actually selected.
  const label = selectedSnapshotId
    ? (selectedSnapshot?.name ?? selectedSnapshotId)
    : selected
      ? displayBlueprintName(selected.name)
      : 'No Blueprint';
  const options: ConfigChipSelectOption<string>[] = [
    {
      key: NONE_BLUEPRINT,
      value: NONE_BLUEPRINT,
      label: <span className="text-muted-foreground">No Blueprint</span>,
      searchValue: '__none__ No Blueprint',
    },
    ...blueprints.map((bp) => ({
      key: bp.id,
      value: bp.id,
      label: displayBlueprintName(bp.name),
      searchValue: `${displayBlueprintName(bp.name)} ${bp.id}`,
      icon: <Package className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />,
      trailing: bp.metadata?.repo ? (
        <span className="shrink-0 text-2xs text-muted-foreground">({bp.metadata.repo})</span>
      ) : undefined,
      group: 'Blueprints',
    })),
    // Snapshots ride in the same picker under their own group. Their option
    // value is the `snapshot:<id>` picker token (not the raw id) so selection
    // round-trips through the same `value`/`onChange` the blueprint rows use.
    ...snapshots.map((snapshot) => ({
      key: snapshot.id,
      value: snapshotPickerValue(snapshot.id),
      label: snapshot.name,
      searchValue: `${snapshot.name} ${snapshot.id}`,
      icon: <Camera className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />,
      group: 'Snapshots',
    })),
  ];
  return (
    <ConfigChipSelect
      ariaLabel="Blueprint"
      triggerLabel={label}
      triggerIcon={
        selectedSnapshot ? (
          <Camera className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <Package className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )
      }
      options={options}
      value={value}
      onChange={onChange}
      searchPlaceholder={
        snapshots.length > 0 ? 'Search Blueprints & snapshots…' : 'Search Blueprints…'
      }
      emptyMessage="No Blueprints found."
    />
  );
}

interface CapabilityToggle {
  key: string;
  /** Chip/label text when this is the (first) enabled capability. */
  label: string;
  description: string;
  icon: LucideIcon;
  /** Kept explicit so existing dockerd assertions ("Toggle Docker-in-Docker") stay stable. */
  toggleAriaLabel: string;
  enabled: boolean;
  onChange: (next: boolean) => void;
}

/**
 * The "Capabilities" chip: a popover of per-launch devbox capability toggles
 * (Docker-in-Docker, computer use). The chip collapses to a dashed "Add"
 * until at least one is enabled, then names the enabled capability (with a
 * `+N` when several are on).
 */
function CapabilitiesChip({ capabilities }: { capabilities: CapabilityToggle[] }) {
  const enabled = capabilities.filter((c) => c.enabled);
  const primary = enabled[0];
  const chipLabel = !primary
    ? 'Add'
    : enabled.length > 1
      ? `${primary.label} +${enabled.length - 1}`
      : primary.label;
  const ChipIcon = primary?.icon ?? Plus;
  return (
    <ConfigChip
      ariaLabel="Edit capabilities"
      dashed={enabled.length === 0}
      label={chipLabel}
      icon={
        <ChipIcon
          className={cn('h-3.5 w-3.5 shrink-0', primary ? 'text-muted-foreground' : undefined)}
        />
      }
    >
      {() => (
        <div className="space-y-0.5 p-1">
          {capabilities.map((cap) => {
            const CapIcon = cap.icon;
            return (
              <button
                key={cap.key}
                type="button"
                aria-pressed={cap.enabled}
                aria-label={cap.toggleAriaLabel}
                data-active={cap.enabled || undefined}
                onClick={() => cap.onChange(!cap.enabled)}
                className={cn(
                  'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted',
                  cap.enabled && 'bg-primary/10',
                )}
              >
                <CapIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="flex-1">
                  <span className="block font-medium">{cap.label}</span>
                  <span className="block text-2xs text-muted-foreground">{cap.description}</span>
                </span>
                {cap.enabled && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
              </button>
            );
          })}
        </div>
      )}
    </ConfigChip>
  );
}

function IdleTimeChip({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (next: number | null) => void;
}) {
  const effective = value ?? DEFAULT_SANDBOX_IDLE_TIME_MINUTES;
  const isPreset = SANDBOX_IDLE_TIME_OPTIONS.some((opt) => opt.value === effective);
  const triggerLabel = `${effective} min`;
  const options: ConfigChipSelectOption<number>[] = [
    ...SANDBOX_IDLE_TIME_OPTIONS.map((opt) => ({
      key: String(opt.value),
      value: opt.value,
      label: opt.label,
      searchValue: `${opt.label} ${opt.value}`,
    })),
    {
      key: 'custom',
      value: IDLE_TIME_CUSTOM_SENTINEL,
      label: <span className="text-muted-foreground">Custom…</span>,
      searchValue: 'custom',
      group: '',
    },
  ];
  // Wrap ConfigChip directly so the popover can switch between the picker
  // list and a custom-value input without coordinating two popovers.
  return (
    <ConfigChip
      ariaLabel="Auto-suspend after"
      label={triggerLabel}
      icon={<Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
    >
      {(close) => (
        <IdleTimePopover
          value={effective}
          isPreset={isPreset}
          options={options}
          onChange={onChange}
          close={close}
        />
      )}
    </ConfigChip>
  );
}

function IdleTimePopover({
  value,
  isPreset,
  options,
  onChange,
  close,
}: {
  value: number;
  isPreset: boolean;
  options: ConfigChipSelectOption<number>[];
  onChange: (next: number | null) => void;
  close: () => void;
}) {
  const [mode, setMode] = useState<'pick' | 'custom'>(isPreset ? 'pick' : 'custom');
  const [draft, setDraft] = useState<string>(String(value));
  // Keep the draft synced when the parent value changes while picking, so
  // switching to Custom shows the current selection as the starting point.
  function commitCustom() {
    const parsed = Number.parseInt(draft, 10);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    onChange(parsed === DEFAULT_SANDBOX_IDLE_TIME_MINUTES ? null : parsed);
    close();
  }
  if (mode === 'custom') {
    return (
      <div className="space-y-2 p-2">
        <div className="space-y-1">
          <Label htmlFor="idle-time-custom" className="text-2xs">
            Auto-suspend after (minutes)
          </Label>
          <Input
            id="idle-time-custom"
            type="number"
            min={1}
            step={1}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                commitCustom();
              }
            }}
            autoFocus
            className="h-8 text-xs"
          />
        </div>
        <div className="flex justify-end gap-1.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-2xs"
            onClick={() => setMode('pick')}
          >
            Back
          </Button>
          <Button type="button" size="sm" className="h-7 px-2 text-2xs" onClick={commitCustom}>
            Save
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="p-1">
      {options.map((opt) => {
        const selected = opt.value !== IDLE_TIME_CUSTOM_SENTINEL && opt.value === value;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => {
              if (opt.value === IDLE_TIME_CUSTOM_SENTINEL) {
                setDraft(String(value));
                setMode('custom');
                return;
              }
              onChange(opt.value === DEFAULT_SANDBOX_IDLE_TIME_MINUTES ? null : opt.value);
              close();
            }}
            className={cn(
              'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted',
              selected && 'bg-primary/10',
            )}
          >
            <span className="flex-1 truncate">{opt.label}</span>
            {selected && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
          </button>
        );
      })}
      {!isPreset && (
        <div className="mt-1 border-t border-border pt-1 text-2xs text-muted-foreground">
          <span className="px-2 py-1">Current: {value} min (custom)</span>
        </div>
      )}
    </div>
  );
}

/**
 * Variables / Secrets chip. Operates on the shared `AgentEnvVar[]` array but
 * scopes its popover list to entries matching the chip variant (plaintext vs
 * secret), so the Advanced bar exposes two focused editors. The popover hosts
 * the existing `AgentEnvVarRow` list plus an in-popover "Add" affordance and
 * renders dashed/empty until at least one matching entry exists.
 */
function EnvVarChip({
  variant,
  envVars,
  onEnvVarsChange,
  hasStoredSecrets,
  secretsOwner,
}: {
  variant: 'variable' | 'secret';
  envVars: AgentEnvVar[];
  onEnvVarsChange: (next: AgentEnvVar[]) => void;
  hasStoredSecrets: boolean;
  secretsOwner?: AvailableSecretsOwner;
}) {
  const isSecret = variant === 'secret';
  const label = isSecret ? 'Secrets' : 'Env vars';
  const [focusIndex, setFocusIndex] = useState<number | null>(null);

  const indices = envVars.reduce<number[]>((acc, entry, i) => {
    if (Boolean(entry.isSecret) === isSecret) acc.push(i);
    return acc;
  }, []);
  const count = indices.length;

  const updateAt = (index: number, patch: Partial<AgentEnvVar>) =>
    onEnvVarsChange(envVars.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));
  const removeAt = (index: number) => onEnvVarsChange(envVars.filter((_, i) => i !== index));
  const add = () => {
    setFocusIndex(envVars.length);
    onEnvVarsChange([
      ...envVars,
      isSecret ? { key: '', isSecret: true } : { key: '', value: '', isSecret: false },
    ]);
  };

  return (
    <ConfigChip
      ariaLabel={label}
      dashed={count === 0}
      label={count > 0 ? String(count) : 'Add'}
      icon={count === 0 ? <Plus className="h-3.5 w-3.5 shrink-0" /> : null}
      data-testid={`advanced-${variant}-chip`}
    >
      {() => (
        <div className="space-y-2 p-2" data-testid={`advanced-${variant}-editor`}>
          {count === 0 ? (
            <p className="px-1 py-1 text-2xs text-muted-foreground">
              No {label.toLowerCase()} yet.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {indices.map((index, n) => (
                <AgentEnvVarRow
                  key={index}
                  entry={envVars[index]!}
                  hasStoredSecrets={hasStoredSecrets}
                  testIdSuffix={`${variant}-${n}`}
                  secretsOwner={secretsOwner}
                  autoFocus={focusIndex === index}
                  onChange={(patch) => updateAt(index, patch)}
                  onRemove={() => removeAt(index)}
                />
              ))}
            </ul>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-full justify-start gap-1.5 px-2 text-2xs text-muted-foreground"
            onClick={add}
            aria-label={`Add ${variant}`}
          >
            <Plus className="h-3.5 w-3.5 shrink-0" />
            Add {variant}
          </Button>
        </div>
      )}
    </ConfigChip>
  );
}

export interface AdvancedConfigChipsProps {
  blueprints: Blueprint[];
  /**
   * Org devbox snapshots offered in the same picker as blueprints. A selected
   * snapshot flows through `blueprintValue` as `snapshot:<snp_id>` (see
   * {@link SNAPSHOT_VALUE_PREFIX}). Omit/empty to hide the group — e.g. for
   * orgs without the `devbox-snapshots` capability.
   */
  snapshots?: Snapshot[];
  blueprintValue: string;
  onBlueprintValueChange: (value: string) => void;
  /** Repo the run clones, used to flag a blueprint built for a different one. */
  repoSlug?: string;
  resourceSize?: ResourceSize | null;
  onResourceSizeChange?: (next: ResourceSize | null) => void;
  /** Selected org-defined custom size, mutually exclusive with `resourceSize`. */
  customSize?: CustomSandboxSize | null;
  onCustomSizeChange?: (next: CustomSandboxSize | null) => void;
  /** The org's custom sizes offered in the size picker. */
  customSizes?: CustomSandboxSize[];
  /** The org's default-size setting; names the size chip's "(Default)" option. */
  defaultSandboxSize?: string | null;
  /** Whether the active Runloop account is subject to the trial size cap. */
  isTrial?: boolean;
  dockerd?: boolean;
  onDockerdChange?: (next: boolean) => void;
  /**
   * Enable computer use / browser use (virtual desktop) for the launch. The
   * toggle only appears when `onComputerUseChange` is supplied — surfaces
   * pass it only when the org has the computer-use plugin installed.
   */
  computerUse?: boolean;
  onComputerUseChange?: (next: boolean) => void;
  /**
   * Minutes the devbox may sit idle before auto-suspending. `null` means
   * "use the provider default" ({@link DEFAULT_SANDBOX_IDLE_TIME_MINUTES}).
   * The chip only renders when `onIdleTimeMinutesChange` is supplied.
   */
  idleTimeMinutes?: number | null;
  onIdleTimeMinutesChange?: (next: number | null) => void;
  /**
   * Combined env-var array (plaintext + secret). When `onEnvVarsChange` is
   * supplied, the Variables and Secrets chips render and edit this array in
   * place. Surfaces that don't support env vars omit the handler.
   */
  envVars?: AgentEnvVar[];
  onEnvVarsChange?: (next: AgentEnvVar[]) => void;
  /** Whether the edited entity already has stored secrets (badge vs entry). */
  hasStoredSecrets?: boolean;
  /** Owner context forwarded to the secret picker inside the Secrets chip. */
  secretsOwner?: AvailableSecretsOwner;
  /**
   * Labels (`alb_*`) the launch puts on the run. The chip only renders when
   * `onLabelIdsChange` is supplied — surfaces with no organization behind them
   * (Storybook fixtures, the blueprint form) omit it.
   */
  labelIds?: string[];
  onLabelIdsChange?: (next: string[]) => void;
  /** Group (`agp_*`) the launch files the run into. Rendered with its handler. */
  groupId?: string | null;
  onGroupIdChange?: (next: string | null) => void;
}

/**
 * Compact chip row for the launcher&rsquo;s Advanced settings — blueprint,
 * devbox size, and devbox capabilities. Each renders as a captioned field: a
 * small label above a minimal chip (icon + value) with a hover card for
 * details and a click popover for editing, plus an `InlineDocsHint` docs
 * button at the end of the label row linking to that control&rsquo;s docs
 * section. Keeps the section dense instead of a stack of full-width
 * selects. Devbox size and capabilities only appear when their handlers are
 * supplied (surfaces that don&rsquo;t support them omit them).
 */
export function AdvancedConfigChips({
  blueprints,
  snapshots,
  blueprintValue,
  repoSlug,
  onBlueprintValueChange,
  resourceSize = null,
  onResourceSizeChange,
  customSize = null,
  onCustomSizeChange,
  customSizes,
  defaultSandboxSize = null,
  isTrial = false,
  dockerd = false,
  onDockerdChange,
  computerUse = false,
  onComputerUseChange,
  idleTimeMinutes = null,
  onIdleTimeMinutesChange,
  envVars,
  onEnvVarsChange,
  hasStoredSecrets = false,
  secretsOwner,
  labelIds,
  onLabelIdsChange,
  groupId = null,
  onGroupIdChange,
}: AdvancedConfigChipsProps) {
  const chipFieldClassName = 'max-sm:[&_button]:w-full';

  // A blueprint bakes in its own devbox size; surface it as the size chip's
  // inherited default so an unmodified launch uses that size. A base blueprint
  // is a substrate (its build size isn't a launch intent) and snapshots carry
  // no size here, so neither contributes a size to inherit.
  const selectedBlueprint = blueprints.find((bp) => bp.id === blueprintValue) ?? null;
  const blueprintForSize =
    selectedBlueprint && !isBaseBlueprint(selectedBlueprint) ? selectedBlueprint : null;
  // Only a repo-scoped blueprint can be wrong for the repo: an unscoped one
  // (`base` and friends) bakes in no repo at all, so it fits any run.
  const selectedBlueprintRepo = selectedBlueprint?.metadata?.repo;
  const mismatchedBlueprintRepo =
    repoSlug && selectedBlueprintRepo && !blueprintMatchesRepo(selectedBlueprint, repoSlug)
      ? selectedBlueprintRepo
      : null;
  return (
    <div className="grid grid-cols-2 items-end gap-x-3 gap-y-3 sm:flex sm:flex-wrap sm:gap-x-4">
      <ConfigChipField
        label="Blueprint"
        docsSlug={BLUEPRINT_DOCS_SLUG}
        className={chipFieldClassName}
      >
        <div className="flex min-w-0 items-center gap-1.5">
          <BlueprintChip
            blueprints={blueprints}
            snapshots={snapshots}
            value={blueprintValue}
            onChange={onBlueprintValueChange}
          />
          {mismatchedBlueprintRepo && repoSlug && (
            <BlueprintRepoMismatchHint
              blueprintRepo={mismatchedBlueprintRepo}
              repoSlug={repoSlug}
            />
          )}
        </div>
      </ConfigChipField>
      {onResourceSizeChange && (
        <ConfigChipField
          label="Sandbox Size"
          docsSlug={DEVBOX_SIZE_DOCS_SLUG}
          className={chipFieldClassName}
        >
          <DevboxSizeChip
            value={resourceSize}
            onChange={onResourceSizeChange}
            customSize={customSize}
            onCustomSizeChange={onCustomSizeChange}
            customSizes={customSizes}
            defaultSandboxSize={defaultSandboxSize}
            isTrial={isTrial}
            blueprintResourceSize={blueprintForSize?.resourceSize ?? null}
            blueprintCustomSize={blueprintForSize?.customSize ?? null}
            blueprintName={blueprintForSize ? displayBlueprintName(blueprintForSize.name) : null}
          />
        </ConfigChipField>
      )}
      {(onDockerdChange || onComputerUseChange) && (
        <ConfigChipField
          label="Capabilities"
          docsSlug={CAPABILITIES_DOCS_SLUG}
          className={chipFieldClassName}
        >
          <CapabilitiesChip
            capabilities={[
              ...(onDockerdChange
                ? [
                    {
                      key: 'dockerd',
                      label: 'dockerd',
                      description: 'Docker-in-Docker for building containers',
                      icon: Container,
                      toggleAriaLabel: 'Toggle Docker-in-Docker',
                      enabled: dockerd,
                      onChange: onDockerdChange,
                    },
                  ]
                : []),
              ...(onComputerUseChange
                ? [
                    {
                      key: 'computerUse',
                      label: 'computer use',
                      description: 'Virtual desktop for browser & computer use',
                      icon: MonitorPlay,
                      toggleAriaLabel: 'Toggle computer use',
                      enabled: computerUse,
                      onChange: onComputerUseChange,
                    },
                  ]
                : []),
            ]}
          />
        </ConfigChipField>
      )}
      {onIdleTimeMinutesChange && (
        <ConfigChipField
          label="Auto-suspend after"
          docsSlug={IDLE_TIME_DOCS_SLUG}
          className={chipFieldClassName}
        >
          <IdleTimeChip value={idleTimeMinutes} onChange={onIdleTimeMinutesChange} />
        </ConfigChipField>
      )}
      {onEnvVarsChange && (
        <>
          <ConfigChipField
            label="Env vars"
            docsSlug={ENV_VARS_DOCS_SLUG}
            className={chipFieldClassName}
          >
            <EnvVarChip
              variant="variable"
              envVars={envVars ?? []}
              onEnvVarsChange={onEnvVarsChange}
              hasStoredSecrets={hasStoredSecrets}
              secretsOwner={secretsOwner}
            />
          </ConfigChipField>
          <ConfigChipField
            label="Secrets"
            docsSlug={SECRETS_DOCS_SLUG}
            className={chipFieldClassName}
          >
            <EnvVarChip
              variant="secret"
              envVars={envVars ?? []}
              onEnvVarsChange={onEnvVarsChange}
              hasStoredSecrets={hasStoredSecrets}
              secretsOwner={secretsOwner}
            />
          </ConfigChipField>
        </>
      )}
      {onLabelIdsChange && (
        <ConfigChipField label="Labels" className={chipFieldClassName}>
          <AgentLabelsChip value={labelIds ?? []} onChange={onLabelIdsChange} />
        </ConfigChipField>
      )}
      {onGroupIdChange && (
        <ConfigChipField label="Group" className={chipFieldClassName}>
          <AgentGroupChipPicker value={groupId} onChange={onGroupIdChange} />
        </ConfigChipField>
      )}
    </div>
  );
}
